import { AlertTriangle } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '../components/button';
import { cn } from '../lib/cn';

export interface ImpersonationBannerProps {
  /** `impersonation.impersonatorEmail` from `GET /auth/session`. */
  impersonatorEmail: string;
  /** `impersonation.expiresAt`, ISO 8601. The countdown runs to this. */
  expiresAt: string;
  /** `impersonation.blockedPermissions`. Summarised in plain words, never as raw keys. */
  blockedPermissions?: readonly string[];
  /** Who is being viewed as, when the app knows the name. */
  targetLabel?: ReactNode;
  /** The clinic the support session is running in. */
  tenantName?: ReactNode;
  /** `DELETE /api/v1/platform/impersonation/current`, then refresh the session. */
  onExit: () => void;
  exiting?: boolean;
  /** Injected by tests so the countdown is deterministic. Defaults to the wall clock. */
  now?: () => number;
  className?: string;
}

/** The wall clock, as the rest of the platform's front end spells it (TESTING.md §2.1). */
const DEFAULT_NOW = (): number => new Date().getTime();

/**
 * The impersonation banner (M2.8, ADR-005, SECURITY.md §6.3, rules §20.5, §20.6).
 *
 * Four things are required of it and each one is here for a reason that is not
 * decoration:
 *
 * - **It is always visible while impersonation is active.** It renders above the top
 *   bar, on every screen, in the loudest tone the design system has. Someone who forgets
 *   they are impersonating is the whole risk this control exists to manage.
 * - **Exit sits next to the statement.** Leaving must never be harder than entering
 *   (rules §20.6), which is also why `DELETE /platform/impersonation/current` is
 *   deliberately not permission-gated or MFA-gated on the server.
 * - **The expiry is a live countdown**, not a timestamp. "Ends at 14:32" needs mental
 *   arithmetic; "ends in 12 minutes" does not, and the session really does end on its
 *   own after `SESSION_IMPERSONATION_MINUTES`.
 * - **The blocked actions are named**, from `blockedPermissions` on the session summary,
 *   so a refusal later is expected rather than mysterious. The list is the server's; this
 *   only translates it.
 *
 * The countdown is deliberately *not* inside the live region: a `role="status"` that
 * changes every second would be read aloud every second. The statement announces once;
 * the remaining time is available on demand.
 */
export function ImpersonationBanner({
  impersonatorEmail,
  expiresAt,
  blockedPermissions,
  targetLabel,
  tenantName,
  onExit,
  exiting = false,
  now = DEFAULT_NOW,
  className,
}: ImpersonationBannerProps) {
  /*
   * The clock is the state; the remaining time is derived from it during render.
   *
   * The obvious shape - `remaining` in state, recomputed in an effect whenever
   * `expiresAt` changes - sets state from an effect body, which costs a second render
   * pass for something that was never anything but a calculation. Holding the *time*
   * instead means a new `expiresAt` is reflected on the very next render with no effect
   * involved, and the only thing the effect does is subscribe to the tick.
   */
  const [nowMs, setNowMs] = useState(now);
  const [showBlocked, setShowBlocked] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
    // `now` is a test seam and stable in the app; re-subscribing every render would
    // reset the tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remaining = secondsUntil(expiresAt, nowMs);
  const expired = remaining <= 0;
  const blocked = (blockedPermissions ?? []).map(describePermission);

  return (
    <div
      className={cn(
        'border-b border-warning-border bg-warning-subtle px-6 py-3 text-sm text-warning-ink max-[480px]:px-4',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="flex min-w-0 items-start gap-[10px]">
          {/* Not colour alone (rules §48.5): the tone carries an icon and a word. */}
          <AlertTriangle
            size={16}
            strokeWidth={2}
            aria-hidden={true}
            className="mt-[2px] shrink-0 text-warning"
          />
          <span className="min-w-0">
            <span className="font-semibold">
              {targetLabel === undefined ? 'Support session' : <>Viewing as {targetLabel}</>}
              {tenantName === undefined ? null : <> at {tenantName}</>}
            </span>{' '}
            Signed in as {impersonatorEmail}. Everything you do is recorded against your own
            account.
          </span>
        </p>

        <span className="flex shrink-0 items-center gap-3">
          <span className="text-sm">
            <span aria-hidden="true">
              {expired ? 'Session ended' : `Ends in ${formatRemaining(remaining)}`}
            </span>
            <span className="sr-only">
              {expired
                ? 'This support session has ended.'
                : `This support session ends in ${formatRemaining(remaining)}.`}
            </span>
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={onExit}
            loading={exiting}
            loadingLabel="Leaving"
          >
            Exit
          </Button>
        </span>
      </div>

      {blocked.length === 0 ? null : (
        <div className="mt-1 pl-[26px]">
          <button
            type="button"
            aria-expanded={showBlocked}
            onClick={() => {
              setShowBlocked((current) => !current);
            }}
            className="cursor-pointer text-sm underline underline-offset-2"
          >
            {showBlocked ? 'Hide what is blocked' : `${String(blocked.length)} actions are blocked`}
          </button>
          {showBlocked ? (
            <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {blocked.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}

function secondsUntil(iso: string, from: number): number {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.round((at - from) / 1000));
}

/** "12 minutes", "45 seconds", "1 hour 5 minutes". Never a bare number. */
export function formatRemaining(seconds: number): string {
  if (seconds < 60) return `${String(seconds)} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourText = `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  return rest === 0 ? hourText : `${hourText} ${String(rest)} minute${rest === 1 ? '' : 's'}`;
}

/**
 * A permission key in the words of the person reading the banner.
 *
 * `IMPERSONATION_BLOCKED_PERMISSIONS` in `@platform/contracts` is the source of the list;
 * this is only the phrasing. An unknown key falls back to a readable form of itself
 * rather than being dropped, so a key added server-side still appears here.
 */
export function describePermission(key: string): string {
  const known: Record<string, string> = {
    'payments.charge': 'Taking payments',
    'payments.adjust': 'Adjusting amounts',
    'payments.void': 'Voiding charges',
    'payments.refund': 'Refunds',
    'payment_methods.manage': 'Card details',
    'patients.export': 'Patient exports',
    'appointments.export': 'Appointment exports',
    'reports.export': 'Report exports',
    'integrations.connect': 'Connecting integrations',
    'integrations.rotate_credentials': 'Rotating credentials',
    'tenant.users.manage': 'Managing staff',
    'tenant.roles.assign': 'Changing roles',
  };
  return known[key] ?? key.replace(/[._]/g, ' ');
}
